import { useLocation, useNavigate } from 'react-router-dom'
import { useCallback } from 'react'
import { motion } from 'framer-motion'
import { Home, Grid3X3, User } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'

const BottomNavbar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isProfileComplete } = useAuth()

  const profileComplete = isProfileComplete()

  const navItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      path: '/dashboard',
      isActive: location.pathname === '/dashboard'
    },
    {
      id: 'projects', 
      label: 'Projects',
      icon: Grid3X3,
      path: '/projects',
      isActive: location.pathname === '/projects'
    },
    {
      id: 'profile',
      label: 'Profile', 
      icon: User,
      path: '/profile',
      isActive: location.pathname === '/profile'
    }
  ]

  const handleNavigation = useCallback((path) => {
    navigate(path)
  }, [navigate])

  return (
    <motion.nav
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-safe"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.08) 100%)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
      }}
    >
      {/* Top gradient line */}
      <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      
      <div className="max-w-sm mx-auto">
        <div className="relative flex justify-between items-center py-4 px-6 rounded-2xl mt-2 mb-4"
          style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.1)'
          }}
        >
          {navItems.map((item, index) => {
            const IconComponent = item.icon
            
            return (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  duration: 0.4, 
                  delay: index * 0.1,
                  ease: "easeOut"
                }}
                onClick={() => handleNavigation(item.path)}
                className="relative flex flex-col items-center justify-center group touch-manipulation"
                whileTap={{ scale: 0.92 }}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {/* Active background glow */}
                {item.isActive && (
                  <motion.div
                    layoutId="activeNavGlow"
                    className="absolute inset-0 rounded-xl opacity-20"
                    style={{
                      background: 'radial-gradient(ellipse at center, rgba(20,184,166,0.4) 0%, transparent 70%)',
                      filter: 'blur(8px)'
                    }}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                
                {/* Icon container */}
                <motion.div
                  whileHover={{ scale: 1.05, y: -1 }}
                  className={`
                    relative p-3 rounded-xl transition-all duration-300
                    ${item.isActive 
                      ? 'bg-primary-teal/20 shadow-lg' 
                      : 'group-hover:bg-white/10'
                    }
                  `}
                  style={item.isActive ? {
                    boxShadow: '0 4px 20px rgba(20,184,166,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                  } : {}}
                >
                  <IconComponent
                    className={`
                      w-5 h-5 transition-all duration-300
                      ${item.isActive
                        ? 'text-primary-teal drop-shadow-sm'
                        : 'text-neutral-400 group-hover:text-primary-teal group-active:text-primary-teal/80'
                      }
                    `}
                  />
                  
                  {/* Profile completion indicator for Profile tab */}
                  {item.id === 'profile' && !profileComplete && (
                    <motion.div
                      className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full"
                      style={{
                        boxShadow: '0 0 6px rgba(245, 158, 11, 0.6)'
                      }}
                      transition={{ type: "spring", bounce: 0.4, duration: 0.6 }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                    />
                  )}
                  
                  {/* Active indicator dot */}
                  {item.isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute -top-1 -right-1 w-2 h-2 bg-primary-teal rounded-full"
                      style={{
                        boxShadow: '0 0 6px rgba(20,184,166,0.6), 0 0 12px rgba(20,184,166,0.4)'
                      }}
                      transition={{ type: "spring", bounce: 0.4, duration: 0.6 }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                    />
                  )}
                </motion.div>
                
                {/* Label */}
                <motion.span 
                  className={`
                    text-xs font-medium mt-1.5 transition-all duration-300 select-none
                    ${item.isActive 
                      ? 'text-primary-teal' 
                      : 'text-neutral-400 group-hover:text-neutral-300 group-active:text-primary-teal/80'
                    }
                  `}
                  animate={{ 
                    opacity: item.isActive ? 1 : 0.8,
                    y: item.isActive ? 0 : 1
                  }}
                  transition={{ duration: 0.2 }}
                >
                  {item.label}
                </motion.span>
                
                {/* Ripple effect on tap */}
                <motion.div
                  className="absolute inset-0 rounded-xl pointer-events-none"
                  initial={false}
                  whileTap={{
                    background: "radial-gradient(circle, rgba(20,184,166,0.2) 0%, transparent 70%)",
                    scale: 1
                  }}
                  transition={{ duration: 0.1 }}
                />
              </motion.button>
            )
          })}
        </div>
      </div>
      
      {/* Safe area spacer for devices with home indicator */}
      <div className="h-safe-bottom" />
    </motion.nav>
  )
}

export default BottomNavbar
